const InclusiveRightChar = '[';
type InclusiveRight = typeof InclusiveRightChar;
const InclusiveLeftChar = ']';
type InclusiveLeft = typeof InclusiveLeftChar;
type Inclusive = InclusiveRight | InclusiveLeft;
type BPRightArgs = [arg1: InclusiveRight, value: number];
type BPLeftArgs = [value: number, arg2: InclusiveLeft];
type BPStringDefinition = `${number}]` | `[${number}`;

export function BP(breakpoint: BPStringDefinition): BreakPointNode;
/**
 * Create a breakpoint node for a left inclusive breakpoint
 */
export function BP(...args: BPLeftArgs): BreakPointNode;
/**
 * Create a breakpoint node for a right inclusive breakpoint
 */
export function BP(...args: BPRightArgs): BreakPointNode;
export function BP(...args: BPLeftArgs | BPRightArgs | [BPStringDefinition]) {
  if (args.length === 1) {
    const [singleArg] = args;
    const value = Number(singleArg.replace(/[[\]]/, ''));
    const inclusive = singleArg.startsWith(InclusiveRightChar)
      ? InclusiveRightChar
      : InclusiveLeftChar;
    return new BreakPointNode(value, inclusive);
  }
  const [a1, a2] = args;
  const [value, inclusive] = a1 === InclusiveRightChar ? [a2, a1] : [a1, a2];
  return new BreakPointNode(value, inclusive);
}
class BreakPointNode {
  inclusive: Inclusive;
  value: number;
  constructor(value: number, inclusive: Inclusive) {
    this.inclusive = inclusive;
    this.value = value;
  }
  toString() {
    return this.inclusive === InclusiveRightChar
      ? `${InclusiveRightChar}${this.value}`
      : `${this.value}${InclusiveLeftChar}`;
  }
}

export function V<const Value>(value: Value) {
  return new ValueNode(value);
}
class ValueNode<Value> {
  value: Value;
  constructor(value: Value) {
    this.value = value;
  }

  toString() {
    return `${String(this.value)}`;
  }
}
export type Line<Value> = [
  ValueNode<Value>,
  ...(BreakPointNode | ValueNode<Value>)[],
];

/**
 * Create a breakpoints instance from a line of nodes
 * @param line
 * @returns BreakPoints instance
 */
export const BreakPoints = Object.assign(
  <const Value, const BreakpointUnit = unknown>(
    line: Line<Value>,
    options?: BreakPointOptions<BreakpointUnit>,
  ) => new BreakPointerClass(line, options),
  {
    BreakPoint: BP,
    Value: V,
  },
);

type BreakPointOptions<BreakpointUnit> = {
  /**
   * If mixing different BreakPoint instances and you need to keep track of the unit for the breakpoints, use this option.
   * to know what unit the breakpoints are in. If not provided, the unit will be undefined.
   * for example, if you have two BreakPoint instances with different units, you can use this option to know what unit to use for .getValue().
   */
  unit?: BreakpointUnit;
  /**
   * If you want to mirror the breakpoints to the values, use this option.
   * Using this will add validation on BreakPoint initialization to check that all breakpoints are positive.
   */
  mirror?: boolean;
};
export type BreakPointsInstance<Value, BreakpointUnit> = ReturnType<
  typeof BreakPoints<Value, BreakpointUnit>
>;
export class BreakPointerClass<const Value, const BreakpointUnit> {
  private readonly line: Line<Value>;
  public readonly breakpointUnit: BreakpointUnit;
  public readonly mirrored: boolean;
  private readonly breakPointList: BreakPointNode[] = [];
  private readonly valueNodeList: ValueNode<Value>[] = [];
  private readonly breakPointSet: Set<BreakPointNode> = new Set();
  private readonly valueNodeSet: Set<ValueNode<Value>> = new Set();
  private static readonly BP_START = BP('[', -Infinity);
  private static readonly BP_END = BP(Infinity, ']');
  constructor(line: Line<Value>, options?: BreakPointOptions<BreakpointUnit>) {
    this.line = line;
    this.breakpointUnit =
      options?.unit ?? (undefined as unknown as BreakpointUnit);
    this.mirrored = options?.mirror ?? false;
    this.validateLine(line, options);
    this.breakPointList = line.filter(this.isBreakPointNode);
    this.valueNodeList = line.filter(
      (node): node is ValueNode<Value> => !this.isBreakPointNode(node),
    );
    this.breakPointSet = new Set(this.breakPointList);
    this.valueNodeSet = new Set(this.valueNodeList);
  }
  private validateLine(
    line: Line<Value>,
    options: BreakPointOptions<BreakpointUnit> | undefined,
  ) {
    if (line.length % 2 === 0)
      throw new Error('Line must have an odd number of nodes');

    if (options?.mirror === true) {
      line.forEach((node, index) => {
        if (node instanceof BreakPointNode && node.value < 0) {
          throw new Error(
            `For mirroring, all breakpoints must be positive. Node at index ${index} is negative: ${node.toString()}`,
          );
        }
      });
    }

    // check for increasing breakpoints and odd/even index for value and breakpoint nodes.
    line.reduce<BreakPointNode>((lastBreakPoint, node, index) => {
      const type = index % 2 === 0 ? 'even (value)' : 'odd (breakpoint)';
      switch (type) {
        case 'even (value)': {
          if (node instanceof BreakPointNode)
            throw new Error(
              `Breakpoint nodes must be at odd index. Index ${index} is '${node.toString()}'`,
            );
          return lastBreakPoint;
        }
        case 'odd (breakpoint)': {
          if (node instanceof ValueNode)
            throw new Error(
              `Value nodes must be at even index. Index ${index} is '${node.toString()}'`,
            );

          if (lastBreakPoint.value < node.value) return node;

          const start = index - 2;
          const end = index + 1;
          const errorLine: (BreakPointNode | ValueNode<Value> | string)[] =
            line.slice();
          errorLine.splice(
            start,
            end - start,
            ...[' THIS ==> ', ...line.slice(start, end), ' <== THIS '],
          );
          const compareLine = `node ${start}: ${lastBreakPoint.value}, node ${index}: ${node.value}`;
          throw new Error(
            [
              `Invalid line. Breakpoints must be in increasing order.`,
              `Line:  ${this.getLineString(errorLine)}`,
              `Compare: ${compareLine}`,
            ].join('\n'),
          );
        }
      }
    }, BreakPointerClass.BP_START);
  }
  private getLineString(
    line: readonly (BreakPointNode | ValueNode<Value> | string)[],
  ) {
    return line
      .map((node) => {
        if (typeof node === 'string') return node;
        if (node instanceof ValueNode) return `"${String(node.value)}"`;
        switch (node.inclusive) {
          case '[':
            return `< ${node.value} <=`;
          case ']':
            return `<= ${node.value} <`;
        }
      })
      .join(' ');
  }

  public getBreakPoints(): readonly BreakPointNode[] {
    return [...this.breakPointList];
  }

  public getValues(): readonly ValueNode<Value>[] {
    return [...this.valueNodeList];
  }

  public toString(line?: Line<Value>) {
    return this.getLineString(line ?? this.line);
  }

  getValue(inputValue: number): Value {
    const checkValue = this.mirrored ? Math.abs(inputValue) : inputValue;
    const line = this.line;
    const bpStart = BreakPointerClass.BP_START;
    const bpEnd = BreakPointerClass.BP_END;
    for (let i = 0; i < line.length; i += 2) {
      const before = (line[i - 1] ?? bpStart) as BreakPointNode;
      const valueNode = line[i] as ValueNode<Value>;
      const after = (line[i + 1] ?? bpEnd) as BreakPointNode;

      const beforeOK =
        (before.inclusive === '[' && before.value <= checkValue) ||
        (before.inclusive === ']' && before.value < checkValue);

      const afterOK =
        (after.inclusive === '[' && checkValue < after.value) ||
        (after.inclusive === ']' && checkValue <= after.value);

      if (beforeOK && afterOK) return valueNode.value;
    }
    throw new Error(`No value found for ${inputValue}`); // should never happen
  }

  /**
   * Check if a node is a ValueNode.
   * This method cannot infer the Value type.
   * @param node node to check
   * @returns true if the node is a ValueNode
   */
  public isValueNode(this: void, node: unknown): node is ValueNode<unknown> {
    return node instanceof ValueNode;
  }

  /**
   * Check if a node is a ValueNode.
   * This method can infer the Value type (same as the BreakPointsInstance Value type).
   * @param node node to check
   * @returns true if the node is a ValueNode
   */
  public isOwnValueNode(node: unknown): node is ValueNode<Value> {
    return this.valueNodeSet.has(node as ValueNode<Value>);
  }

  /**
   * Check if a node is a BreakPointNode.
   * @param node node to check
   * @returns true if the node is a BreakPointNode
   */
  public isBreakPointNode(this: void, node: unknown): node is BreakPointNode {
    return node instanceof BreakPointNode;
  }
}
